import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../components/Sidebar";
import Card from "../components/Card";
import { WalletIcon, ChartIcon, ArrowUpIcon, ArrowDownIcon } from "../components/Icons";
import { userAPI, walletAPI } from "../services/api";
import { formatIncomeSourceKey } from "../utils/incomeLabels";

const IncomeBonus = () => {
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const [incomeRes, withdrawalRes] = await Promise.all([
          userAPI.getIncomeSummary(),
          walletAPI.getWithdrawals({ limit: 20 }),
        ]);
        setSummary(incomeRes.summary);
        setEntries(incomeRes.recentEntries || []);
        setWithdrawals(withdrawalRes?.data?.withdrawals || []);
      } catch (err) {
        setError(err?.error || "Failed to load income data");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const bySource = useMemo(() => summary?.bySource || [], [summary]);

  const summaryStats = [
    {
      icon: <WalletIcon className="w-6 h-6" />,
      title: "Total Income",
      amount: `$${Number(summary?.totals?.totalEarned || 0).toFixed(2)}`,
      subtext: "All credited income",
    },
    {
      icon: <ChartIcon className="w-6 h-6" />,
      title: "Working Income (4x cap)",
      amount: `$${Number(summary?.totals?.workingEarned || 0).toFixed(2)}`,
      subtext: `Remaining: $${Number(summary?.totals?.workingRemaining || 0).toFixed(2)}`,
    },
    {
      icon: <ArrowUpIcon className="w-6 h-6" />,
      title: "Non-Working Income (3x cap)",
      amount: `$${Number(summary?.totals?.nonWorkingEarned || 0).toFixed(2)}`,
      subtext: `Remaining: $${Number(summary?.totals?.nonWorkingRemaining || 0).toFixed(2)}`,
    },
    {
      icon: <ArrowDownIcon className="w-6 h-6" />,
      title: "Principal Base",
      amount: `$${Number(summary?.principalBase || 0).toFixed(2)}`,
      subtext: "Used for cap calculation",
    },
  ];

  const getStatusStyle = (status) => {
    switch (String(status || "").toUpperCase()) {
      case "COMPLETED":
      case "CONFIRMED":
      case "CREDITED":
        return "bg-gradient-to-r from-[#22c55e]/20 to-[#16a34a]/20 text-primary border border-primary/30";
      case "PENDING":
      case "PROCESSING":
        return "bg-yellow-500/20 text-yellow-400 border border-yellow-400/30";
      case "FAILED":
      case "REJECTED":
        return "bg-red-500/20 text-red-400 border border-red-400/30";
      default:
        return "bg-gray-500/20 text-[var(--text-secondary)] border border-gray-400/30";
    }
  };

  return (
    <div className="flex min-h-screen bg-[var(--bg-main)]">
      <Sidebar />
      <div className="flex-1 p-4 md:p-8 md:ml-64">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
            Incentives & Rewards
          </h1>
          <p className="text-[var(--text-primary)] text-sm md:text-base">
            Track incentives, rewards, and payout history
          </p>
        </div>

        {loading ? <p className="text-[var(--text-primary)] mb-4">Loading income summary...</p> : null}
        {error ? <p className="text-red-400 mb-4">{error}</p> : null}

        {/* Summary Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {summaryStats.map((stat, index) => (
            <Card key={index}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-sm text-[var(--text-primary)] opacity-70 mb-2">
                    {stat.title}
                  </p>
                  <p className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
                    {stat.amount}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#22c55e]/20 to-[#16a34a]/20 flex items-center justify-center flex-shrink-0">
                  <div className="bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
                    {stat.icon}
                  </div>
                </div>
              </div>
                  <p className="text-xs text-[var(--text-primary)] opacity-60">{stat.subtext}</p>
            </Card>
          ))}
        </div>

        {/* Recent Trading Income */}
        <Card className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
              Income Sources
            </h2>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-primary/20">
                  <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                    Source
                  </th>
                  <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                    Entries
                  </th>
                  <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                    Amount
                  </th>
                  <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {bySource.map((item, index) => (
                  <tr
                    key={index}
                    className="border-b border-primary/10 last:border-0 hover:bg-[#22c55e]/5 transition-colors"
                  >
                    <td className="py-4 px-4 text-[var(--text-primary)]">
                      {formatIncomeSourceKey(item.source)}
                    </td>
                    <td className="py-4 px-4 text-[var(--text-primary)]">{item.entries}</td>
                    <td className="py-4 px-4">
                      <span className="font-semibold bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
                        ${Number(item.totalNet || 0).toFixed(2)}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="px-3 py-1 rounded-lg text-xs font-medium bg-[#22c55e]/10 text-primary border border-primary/20">
                        Active
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-4">
            {bySource.map((item, index) => (
              <div
                key={index}
                className="p-4 rounded-xl bg-[var(--bg-card)] border border-primary/10"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-sm text-[var(--text-primary)] mb-1">
                      {formatIncomeSourceKey(item.source)}
                    </p>
                    <p className="text-xs text-[var(--text-primary)] opacity-70">
                      Entries: {item.entries}
                    </p>
                  </div>
                  <span className="px-3 py-1 rounded-lg text-xs font-medium bg-[#22c55e]/10 text-primary border border-primary/20">
                    Active
                  </span>
                </div>
                <p className="text-2xl font-bold bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
                  ${Number(item.totalNet || 0).toFixed(2)}
                </p>
              </div>
            ))}
          </div>
        </Card>

        {/* Bonus Earning Breakdown */}
        <Card className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
              Compensation Ledger (Recent)
            </h2>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-primary/20">
                  <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                    Date
                  </th>
                  <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                    Bonus Type
                  </th>
                  <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                    Source
                  </th>
                  <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {entries.map((item, index) => (
                  <tr
                    key={index}
                    className="border-b border-primary/10 last:border-0 hover:bg-[#22c55e]/5 transition-colors"
                  >
                    <td className="py-4 px-4 text-[var(--text-primary)]">
                      {new Date(item.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-4 px-4 text-[var(--text-primary)]">
                      {formatIncomeSourceKey(item.source)}
                    </td>
                    <td className="py-4 px-4 text-[var(--text-primary)]">
                      {formatIncomeSourceKey(item.classification)}
                    </td>
                    <td className="py-4 px-4">
                      <span className="font-semibold bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
                        ${Number(item.netAmount || 0).toFixed(2)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-4">
            {entries.map((item, index) => (
              <div
                key={index}
                className="p-4 rounded-xl bg-[var(--bg-card)] border border-primary/10"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-sm text-[var(--text-primary)] mb-1">
                      {formatIncomeSourceKey(item.source)}
                    </p>
                    <p className="text-xs text-[var(--text-primary)] opacity-70">
                      {new Date(item.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="mb-3">
                  <p className="text-2xl font-bold bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
                    ${Number(item.netAmount || 0).toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[var(--text-primary)] opacity-70 mb-1">Source</p>
                  <p className="text-sm text-[var(--text-primary)]">
                    {formatIncomeSourceKey(item.classification)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Payout History */}
        <Card>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
              Payout History
            </h2>
            <button className="text-sm text-[var(--text-primary)] hover:underline">
              View All →
            </button>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-primary/20">
                  <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                    Date
                  </th>
                  <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                    Method
                  </th>
                  <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                    Amount
                  </th>
                  <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                    Status
                  </th>
                  <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                    Transaction ID
                  </th>
                </tr>
              </thead>
              <tbody>
                {withdrawals.map((item, index) => (
                  <tr
                    key={index}
                    className="border-b border-primary/10 last:border-0 hover:bg-[#22c55e]/5 transition-colors"
                  >
                    <td className="py-4 px-4 text-[var(--text-primary)]">
                      {new Date(item.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-4 px-4 text-[var(--text-primary)]">{item.network}</td>
                    <td className="py-4 px-4">
                      <span className="font-semibold bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
                        ${Number(item.totalAmount || 0).toFixed(2)}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span
                        className={`px-3 py-1 rounded-lg text-xs font-medium ${getStatusStyle(
                          item.status
                        )}`}
                      >
                        {item.status}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="font-mono text-sm text-[var(--text-primary)]">
                        {item.transactionId || item.id}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-4">
            {withdrawals.map((item, index) => (
              <div
                key={index}
                className="p-4 rounded-xl bg-[var(--bg-card)] border border-primary/10"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-sm text-[var(--text-primary)] mb-1">{item.network}</p>
                    <p className="text-xs text-[var(--text-primary)] opacity-70">
                      {new Date(item.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-lg text-xs font-medium ${getStatusStyle(
                        item.status
                    )}`}
                  >
                    {item.status}
                  </span>
                </div>
                <div className="mb-3">
                  <p className="text-2xl font-bold bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
                    ${Number(item.totalAmount || 0).toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[var(--text-primary)] opacity-70 mb-1">
                    Transaction ID
                  </p>
                  <p className="text-xs font-mono text-[var(--text-primary)]">
                    {item.transactionId || item.id}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default IncomeBonus;
